/**
 * Elenco delle pubblicazioni — tabella del wireframe §6.
 *
 * Colonne: titolo, nodo, anno, stato, ultima modifica, versioni, azioni.
 * I filtri (nodo, anno, stato) stanno nella querystring, cosi' una vista
 * filtrata e' un indirizzo condivisibile e le schede del cruscotto possono
 * puntarci direttamente.
 *
 * I filtri vengono inviati anche all'API, ma l'elenco e' **rifiltrato in
 * pagina**: se l'implementazione del router ignorasse un parametro, la tabella
 * mostrerebbe comunque cio' che l'intestazione dichiara.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { elencaPubblicazioni } from "../api/client";
import type {
  Nodo,
  OriginePubblicazione,
  Pubblicazione,
  StatoPubblicazione,
} from "../api/types";
import { STATI_PUBBLICAZIONE } from "../api/types";

import { CampoScelta } from "../components/campi";
import type { OpzioneScelta } from "../components/campi";
import {
  Avviso,
  Bottone,
  Chip,
  ChipAccessibilita,
  ChipNodo,
  ChipStato,
  Scheletro,
  StatoVuoto,
} from "../components/primitive";
import { ETICHETTA_ORIGINE, ETICHETTA_STATO } from "../lib/etichette";
import { riepilogoAccessibilita, statoDi } from "../lib/accessibilita";
import { ErroriRilevati } from "../components/ErroriRilevati";
import { Modale } from "../components/Modale";
import { useAlberatura } from "../dati/contesto-alberatura";
import {
  catenePerId,
  partiPercorso,
  percorsoAntenati,
  SEPARATORE_PERCORSO,
} from "../lib/alberatura";
import type { PartiPercorso } from "../lib/alberatura";
import { useRisorsa } from "../lib/useRisorsa";
import { IntestazionePagina } from "../layout/Guscio";

export function Pubblicazioni() {
  const [parametri, setParametri] = useSearchParams();
  const { nodi, tuttiINodi, denominazione } = useAlberatura();
  const [dettaglio, setDettaglio] = useState<Pubblicazione | null>(null);

  const nodoFiltro = parametri.get("nodo") ?? "";
  const annoFiltro = parametri.get("anno") ?? "";
  const statoFiltro = (parametri.get("stato") ?? "") as StatoPubblicazione | "";
  // La provenienza e' un filtro locale e non un parametro dell'API: il canale di
  // ingresso deposita bozze come qualunque redattore, e l'elenco che l'API serve
  // e' gia' quello del perimetro di chi guarda. Filtrare qui evita un parametro
  // di query in piu' per una distinzione che riguarda la revisione, non i dati.
  const origineFiltro = (parametri.get("origine") ?? "") as OriginePubblicazione | "";

  useEffect(() => {
    document.title = "Pubblicazioni · Amministrazione trasparente";
  }, []);

  const carica = useCallback(
    (segnale: AbortSignal) =>
      elencaPubblicazioni(
        {
          nodo: nodoFiltro || undefined,
          anno: annoFiltro ? Number(annoFiltro) : undefined,
          stato: statoFiltro || undefined,
        },
        segnale,
      ),
    [nodoFiltro, annoFiltro, statoFiltro],
  );

  // La risorsa si ricarica al cambio dei filtri; il rifiltro locale che segue
  // e' la rete di sicurezza, non il meccanismo principale.
  const { dato, inCaricamento, errore, ricarica } = useRisorsa(carica, [
    nodoFiltro,
    annoFiltro,
    statoFiltro,
  ]);

  const elenco = useMemo(() => {
    const tutte = dato ?? [];
    return tutte.filter(
      (pubblicazione) =>
        (!nodoFiltro || pubblicazione.nodo_id === nodoFiltro) &&
        (!annoFiltro || String(pubblicazione.anno_riferimento) === annoFiltro) &&
        (!statoFiltro || pubblicazione.stato === statoFiltro) &&
        (!origineFiltro || (pubblicazione.origine ?? "interna") === origineFiltro),
    );
  }, [dato, nodoFiltro, annoFiltro, statoFiltro, origineFiltro]);

  const anniDisponibili = useMemo(() => {
    const anni = new Set<number>((dato ?? []).map((pubblicazione) => pubblicazione.anno_riferimento));
    return [...anni].sort((prima, seconda) => seconda - prima);
  }, [dato]);

  const catene = useMemo(() => catenePerId(nodi), [nodi]);

  const opzioniNodo: OpzioneScelta[] = useMemo(
    () =>
      (function raccogli(
        voci: readonly Nodo[],
        percorso: readonly string[] = [],
      ): OpzioneScelta[] {
        // Ricorsiva: con tre livelli un elenco a due lascerebbe fuori dal
        // filtro proprio i nodi su cui si pubblica. L'etichetta porta la catena
        // degli antenati, non il solo rientro: l'albero adottato contiene
        // omonimi allo stesso livello, e due opzioni tipograficamente identiche
        // fanno scegliere a caso il ramo su cui filtrare.
        return voci.flatMap((voce) => {
          const catena = [...percorso, voce.denominazione];
          return [
            {
              valore: voce.id_nodo,
              etichetta: `${"\u00a0".repeat(percorso.length * 3)}${catena.join(SEPARATORE_PERCORSO)}`,
            },
            ...raccogli(voce.figli ?? [], catena),
          ];
        });
      })(nodi),
    [nodi],
  );

  const aggiornaFiltro = (chiave: string, valore: string) => {
    const successivi = new URLSearchParams(parametri);
    if (valore) successivi.set(chiave, valore);
    else successivi.delete(chiave);
    setParametri(successivi, { replace: true });
  };

  const filtriAttivi = Boolean(nodoFiltro || annoFiltro || statoFiltro || origineFiltro);

  return (
    <div className="pila">
      <IntestazionePagina
        sopratitolo="Redazione"
        titolo="Pubblicazioni"
        sottotitolo="Contenuti dell'ente, con lo stato di ciascuno. Una pubblicazione diventa visibile sul portale solo dopo la transizione a «pubblicato», che crea una versione immutabile."
        azioni={
          <Link className="bottone bottone--primario" to="/documenti">
            Carica un documento
          </Link>
        }
      />

      {errore ? (
        <Avviso tono="critico" titolo="Elenco non disponibile">
          {errore.messaggioEsteso}
        </Avviso>
      ) : null}

      <section className="scheda">
        <div className="filtri">
          <CampoScelta
            etichetta="Nodo dell'alberatura"
            valore={nodoFiltro}
            onCambia={(valore) => aggiornaFiltro("nodo", valore)}
            opzioni={opzioniNodo}
            vuoto="Tutti i nodi"
          />
          <CampoScelta
            etichetta="Anno di riferimento"
            valore={annoFiltro}
            onCambia={(valore) => aggiornaFiltro("anno", valore)}
            opzioni={anniDisponibili.map((anno) => ({
              valore: String(anno),
              etichetta: String(anno),
            }))}
            vuoto="Tutti gli anni"
          />
          <CampoScelta
            etichetta="Stato"
            valore={statoFiltro}
            onCambia={(valore) => aggiornaFiltro("stato", valore)}
            opzioni={STATI_PUBBLICAZIONE.map((stato) => ({
              valore: stato,
              etichetta: ETICHETTA_STATO[stato],
            }))}
            vuoto="Tutti gli stati"
          />
          <CampoScelta
            etichetta="Provenienza"
            valore={origineFiltro}
            onCambia={(valore) => aggiornaFiltro("origine", valore)}
            opzioni={[
              { valore: "interna", etichetta: ETICHETTA_ORIGINE.interna },
              { valore: "esterna", etichetta: ETICHETTA_ORIGINE.esterna },
            ]}
            vuoto="Qualunque provenienza"
          />
          <div className="riga">
            {filtriAttivi ? (
              <Bottone variante="fantasma" onClick={() => setParametri({}, { replace: true })}>
                Azzera i filtri
              </Bottone>
            ) : null}
            <Bottone variante="fantasma" onClick={ricarica}>
              Aggiorna
            </Bottone>
          </div>
        </div>

        {inCaricamento ? (
          <div style={{ padding: "1.25rem" }}>
            <Scheletro righe={6} />
          </div>
        ) : elenco.length === 0 ? (
          <StatoVuoto
            titolo={filtriAttivi ? "Nessuna pubblicazione con questi filtri" : "Nessuna pubblicazione"}
            testo={
              filtriAttivi
                ? "Provi ad allargare la selezione, oppure azzeri i filtri."
                : "Il primo contenuto nasce come bozza: si carica il file sull'obbligo e lo si pubblica dall'alberatura, dove il documento si vede al suo posto nell'albero."
            }
            azione={
              filtriAttivi ? null : (
                <Link className="bottone bottone--primario" to="/documenti">
                  Carica un documento
                </Link>
              )
            }
          />
        ) : (
          <div className="tabella-guscio">
            <table className="tabella">
              <caption>
                {elenco.length} pubblicazioni
                {filtriAttivi ? " corrispondenti ai filtri selezionati" : ""}.
              </caption>
              <thead>
                <tr>
                  <th scope="col">Titolo</th>
                  <th scope="col">Nodo</th>
                  <th scope="col">Anno</th>
                  <th scope="col">Stato</th>
                  <th scope="col">Provenienza</th>
                  <th scope="col">Ultima modifica</th>
                  <th scope="col">Versioni</th>
                  <th scope="col">Accessibilita' dei file</th>
                  <th scope="col">
                    <span className="tabella__azioni">Azioni</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {elenco.map((pubblicazione) => {
                  // Le stesse parole del resto del back-office: un secondo
                  // vocabolario («con avvisi», «validata») faceva leggere come
                  // due problemi diversi lo stesso file da correggere.
                  const esito = riepilogoAccessibilita(pubblicazione.documenti ?? []);
                  return (
                    <tr key={pubblicazione.id}>
                      <td className="tabella__principale">{pubblicazione.titolo}</td>
                      <td className="testo-tenue">
                        {pubblicazione.nodo_id ? (
                          <>
                            {percorsoAntenati(catene, pubblicazione.nodo_id) ? (
                              <span className="testo-fioco">
                                {percorsoAntenati(catene, pubblicazione.nodo_id)}
                                <br />
                              </span>
                            ) : null}
                            {denominazione(pubblicazione.nodo_id)}
                          </>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td className="tabella__numero">{pubblicazione.anno_riferimento}</td>
                      <td>
                        <ChipStato stato={pubblicazione.stato} />
                      </td>
                      <td>
                        {(pubblicazione.origine ?? "interna") === "esterna" ? (
                          <>
                            <Chip tono="primario">{ETICHETTA_ORIGINE.esterna}</Chip>
                            {pubblicazione.origine_riferimento ? (
                              <span className="testo-fioco"> {pubblicazione.origine_riferimento}</span>
                            ) : null}
                          </>
                        ) : (
                          <span className="testo-fioco">{ETICHETTA_ORIGINE.interna}</span>
                        )}
                      </td>
                      <td className="dato testo-tenue">
                        {pubblicazione.data_ultimo_aggiornamento ?? "—"}
                      </td>
                      <td className="tabella__numero">{pubblicazione.versioni?.length ?? 0}</td>
                      <td>{esito ? <Chip tono={esito.tono}>{esito.testo}</Chip> : "—"}</td>
                      <td className="tabella__azioni">
                        <Bottone
                          variante="contorno"
                          dimensione="piccolo"
                          onClick={() => setDettaglio(pubblicazione)}
                        >
                          Dettaglio
                        </Bottone>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {tuttiINodi.length === 0 && !inCaricamento ? (
        <Avviso tono="attenzione" titolo="Alberatura non caricata">
          Senza alberatura il filtro per nodo resta vuoto e le denominazioni non possono essere
          risolte.
        </Avviso>
      ) : null}

      <DettaglioPubblicazione
        pubblicazione={dettaglio}
        onChiudi={() => setDettaglio(null)}
        parti={(idNodo) => partiPercorso(catene, idNodo)}
      />
    </div>
  );
}

/** Finestra di dettaglio: metadati, versioni e documenti con esiti. */
function DettaglioPubblicazione({
  pubblicazione,
  onChiudi,
  parti,
}: {
  pubblicazione: Pubblicazione | null;
  onChiudi: () => void;
  /** Percorso spezzato in contesto e denominazione, per i chip dei nodi. */
  parti: (idNodo: string) => PartiPercorso;
}) {
  if (!pubblicazione) return null;

  return (
    <Modale
      titolo={pubblicazione.titolo}
      aperta
      onChiudi={onChiudi}
      pedice={
        <Bottone variante="contorno" onClick={onChiudi}>
          Chiudi
        </Bottone>
      }
    >
      <div className="pila">
        <div className="riga">
          <ChipStato stato={pubblicazione.stato} />
          {/* Il percorso intero, che va a capo: in una pillola su una riga sola
              usciva dalla finestra. */}
          {pubblicazione.nodo_id ? (
            <ChipNodo {...parti(pubblicazione.nodo_id)} separatore={SEPARATORE_PERCORSO} />
          ) : (
            <Chip tono="neutro" pallino={false}>
              nodo non indicato
            </Chip>
          )}
          <Chip tono="neutro" pallino={false}>
            anno {pubblicazione.anno_riferimento}
          </Chip>
          {pubblicazione.id_schema ? (
            <Chip tono="neutro" pallino={false}>
              schema {pubblicazione.id_schema}
            </Chip>
          ) : null}
        </div>

        <div>
          <p className="anteprima__etichetta">Riferimenti normativi</p>
          <p>{pubblicazione.riferimenti_normativi || "—"}</p>
        </div>
        <div>
          <p className="anteprima__etichetta">Obblighi</p>
          <p>{pubblicazione.obblighi || "—"}</p>
        </div>
        {pubblicazione.sezioni_collegate.length > 0 ? (
          <div>
            <p className="anteprima__etichetta">Sezioni collegate</p>
            <div className="riga">
              {/* Le sezioni collegate possono stare a qualunque livello, e fra i
                  219 nodi cinquantadue denominazioni sono ripetute: qui il
                  percorso non e' un di piu', e' cio' che identifica il nodo. */}
              {pubblicazione.sezioni_collegate.map((idNodo) => (
                <ChipNodo
                  key={idNodo}
                  {...parti(idNodo)}
                  separatore={SEPARATORE_PERCORSO}
                />
              ))}
            </div>
          </div>
        ) : null}

        {pubblicazione.versioni && pubblicazione.versioni.length > 0 ? (
          <div>
            <p className="anteprima__etichetta">Versioni</p>
            <p className="campo__aiuto">
              Le versioni precedenti restano accessibili ai loro indirizzi: il sistema non
              sovrascrive e non cancella.
            </p>
            <div className="tabella-guscio">
              <table className="tabella">
                <thead>
                  <tr>
                    <th scope="col">N.</th>
                    <th scope="col">Prima pubblicazione</th>
                    <th scope="col">Ultima modifica</th>
                  </tr>
                </thead>
                <tbody>
                  {pubblicazione.versioni.map((versione) => (
                    <tr key={versione.progressivo}>
                      <td className="tabella__numero">{versione.progressivo}</td>
                      <td className="dato">{versione.data_prima_pubblicazione}</td>
                      <td className="dato">{versione.data_ultima_modifica}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : null}

        {pubblicazione.documenti && pubblicazione.documenti.length > 0 ? (
          <div className="pila-stretta">
            <p className="anteprima__etichetta">Documenti</p>
            {pubblicazione.documenti.map((documento) => (
              <div key={documento.id} className="pila-stretta">
                <div className="file-scelto">
                  <span className="file-scelto__estensione" aria-hidden="true">
                    {documento.formato}
                  </span>
                  <div className="file-scelto__corpo">
                    <div className="file-scelto__nome">{documento.titolo}</div>
                    <ChipAccessibilita stato={statoDi(documento)} />
                    <div className="file-scelto__meta">
                      {documento.formato.toUpperCase()} · {documento.peso}
                      {documento.data_pubblicazione ? ` · ${documento.data_pubblicazione}` : ""}
                    </div>
                    {/* Da quando, fino a quando: e', per il singolo documento,
                        cio' che lo Scadenzario elenca per tutti. */}
                    {documento.archiviato_il ? (
                      <div className="file-scelto__meta">
                        Archiviato il {documento.archiviato_il}: fuori dalla pubblicazione
                        corrente, resta accessibile.
                      </div>
                    ) : documento.fine_pubblicazione ? (
                      <div className="file-scelto__meta">
                        Pubblicato dal {documento.pubblicato_dal ?? "—"} fino al{" "}
                        {documento.fine_pubblicazione}
                      </div>
                    ) : null}
                    {documento.nome_file ? (
                      <div className="file-scelto__meta">{documento.nome_file}</div>
                    ) : null}
                  </div>
                </div>
                <ErroriRilevati documento={documento} />
              </div>
            ))}
          </div>
        ) : null}
      </div>
    </Modale>
  );
}
